// Cognito Auth Integration
// Bridges the external AWS Cognito authentication with the app's
// existing authService so role-based navigation works seamlessly.
//
// Key: Login may return embedded user data (API v2.1) — falls back to GET /auth/user if not present.

import { authService, User, UserRole } from './auth';
import { externalAuthAPI, tokenManager } from './external-api';
import { GROUP_TO_ROLE, ROLE_TO_GROUP } from './external-api-config';
import { bridgeCognitoToAgentSession, agentSessionStorage } from './agent-auth-api';
import type { ExternalUserData } from '../types/external-listing';
import { ensureUserProfile } from './user-profile-api';
import { authStorage } from './storage';
import tokenStorage from '../services/token-storage';

const COGNITO_SESSION_FLAG = 'sp_cognito_session';

/**
 * Extract cognito:groups from a stored JWT token (client-side decode, no verification).
 * This is a fallback when the /auth/user response doesn't include groups.
 */
function extractGroupsFromStoredTokens(): string[] {
  try {
    // Try ID token first (has cognito:groups), then access token
    const idToken = tokenManager.getIdToken();
    const accessToken = tokenManager.getAccessToken();
    
    // Log which tokens are available for debugging
    const rawIdToken = localStorage.getItem('sp_ext_id_token');
    console.log(`[CognitoAuth] extractGroupsFromStoredTokens: rawIdToken=${rawIdToken ? 'present (' + rawIdToken.length + ' chars)' : 'MISSING'}, accessToken=${accessToken ? 'present' : 'MISSING'}, getIdToken()=${idToken ? (idToken === accessToken ? 'SAME AS ACCESS TOKEN (no separate ID token!)' : 'present') : 'MISSING'}`);
    
    for (const [label, token] of [['ID', idToken], ['ACCESS', accessToken]] as [string, string | null][]) {
      if (!token) continue;
      const parts = token.split('.');
      if (parts.length !== 3) continue;
      const payload = JSON.parse(atob(parts[1]));
      const groups = payload['cognito:groups'];
      
      // Log all relevant claims for debugging
      console.log(`[CognitoAuth] ${label} token claims: cognito:groups=${JSON.stringify(groups)}, email=${payload.email}, token_use=${payload.token_use}, cognito:username=${payload['cognito:username']}`);
      
      if (Array.isArray(groups) && groups.length > 0) {
        console.log('[CognitoAuth] Extracted groups from', label, 'token:', groups);
        return groups;
      }
    }
    
    console.warn('[CognitoAuth] No cognito:groups found in any stored token');
  } catch (err) {
    console.warn('[CognitoAuth] Failed to extract groups from stored tokens:', err);
  }
  return [];
}

/**
 * Map Cognito user groups to the app's UserRole type.
 */
function mapGroupsToRole(groups: string[]): UserRole {
  for (const group of groups) {
    const role = GROUP_TO_ROLE[group];
    if (role) return role as UserRole;
  }
  return 'public';
}

/**
 * Build a legacy User object from Cognito user data.
 * If groups is empty/missing, falls back to:
 *   1. Client-side JWT decode for cognito:groups
 *   2. Previously saved user role from localStorage
 */
function cognitoUserToLegacyUser(userData: ExternalUserData): User {
  let groups = userData.groups;

  // Fallback 1: If API didn't return groups, try decoding the stored JWT
  if (!groups || groups.length === 0) {
    console.warn('[CognitoAuth] userData.groups is empty — trying JWT decode fallback');
    groups = extractGroupsFromStoredTokens();
  }

  // Fallback 2: If still empty, check previously saved user for role continuity
  let role = mapGroupsToRole(groups);
  if (role === 'public') {
    const savedUser = authService.getCurrentUser();
    if (savedUser && savedUser.role !== 'public' && savedUser.email === userData.email) {
      console.warn(`[CognitoAuth] No groups found, preserving previous role: ${savedUser.role}`);
      role = savedUser.role;
    }
  }

  console.log(`[CognitoAuth] User ${userData.email}: groups=${JSON.stringify(groups)}, role=${role}`);

  const nameFromJwt = (() => {
    try {
      const idToken = tokenStorage.getIdToken();
      if (idToken) {
        const p = JSON.parse(atob(idToken.split('.')[1]));
        return p.name || [p.given_name, p.family_name].filter(Boolean).join(' ') || '';
      }
    } catch { /* ignore */ }
    return '';
  })();

  const name =
    userData.attributes?.name ||
    [userData.attributes?.given_name, userData.attributes?.family_name]
      .filter(Boolean)
      .join(' ') ||
    nameFromJwt ||
    userData.email.split('@')[0];

  // Prefer sub from JWT attributes (stable UUID).
  // IMPORTANT: decode JWT BEFORE falling back to username/email — username may be an email address
  // (e.g. test-seller@somethingproperty.co.za) which breaks downstream ID validation.
  const sub = userData.attributes?.sub || (() => {
    try {
      const idToken = tokenStorage.getIdToken() || tokenManager.getIdToken?.() || '';
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        return payload.sub || payload['cognito:username'] || '';
      }
    } catch { /* ignore */ }
    return '';
  })() || userData.username || userData.email || '';

  return {
    id: `cognito-${sub}`,
    username: userData.email,
    password: '', // not stored
    role,
    name,
    email: userData.email,
  };
}

/**
 * Login via Cognito and bridge to authService.
 * 1. POST /auth/login → get tokens + embedded user data (per API spec v2.1)
 * 2. Use embedded user data OR fall back to GET /auth/user
 * 3. Ensure KV profile exists (idempotent)
 * 4. Bridge into authService
 */
export async function cognitoLogin(
  email: string,
  password: string,
): Promise<{ user: User; groups: string[] }> {
  try {
    // Step 1: Authenticate and get tokens (with single retry for transient 500s)
    console.log('[CognitoLogin] Attempting login for:', email);
    let loginResult: any = null;
    let lastLoginError: any = null;

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        loginResult = await externalAuthAPI.login(email, password);
        break;
      } catch (loginErr: any) {
        lastLoginError = loginErr;
        // Only retry on 500/502 (transient server error). Don't retry auth failures.
        if (loginErr.isServerError && (loginErr.statusCode === 500 || loginErr.statusCode === 502) && attempt < 2) {
          console.warn(`[CognitoLogin] Login attempt ${attempt} got ${loginErr.statusCode} — retrying in 1s...`);
          await new Promise((r) => setTimeout(r, 1000));
          continue;
        }
        throw loginErr;
      }
    }

    if (!loginResult) {
      throw lastLoginError || new Error('Login failed after retries');
    }

    // Store tokens in tokenStorage so Axios interceptor sets Authorization: Bearer header
    tokenStorage.setTokens({
      idToken: loginResult.idToken,
      accessToken: loginResult.accessToken,
      refreshToken: loginResult.refreshToken,
      expiresIn: loginResult.expiresIn ?? 3600,
    });
    // Also persist idToken to localStorage so Playwright storageState restore works
    // and so fetch-based components (AgentReferrals etc.) can resolve getIdToken() correctly.
    localStorage.setItem('sp_ext_id_token', loginResult.idToken);

    // Step 2: Gather groups from ALL available sources and merge them.
    // The most reliable source is the freshly-stored ID token (cognito:groups claim).
    
    // Source A: Decode groups from the fresh ID token (most reliable — always has cognito:groups)
    const jwtGroups = extractGroupsFromStoredTokens();
    console.log('[CognitoLogin] Source A (JWT decode):', JSON.stringify(jwtGroups));
    
    // Source B: Embedded user data from login response (per API spec v2.1)
    const embeddedUser = loginResult.user;
    const embeddedGroups: string[] = (embeddedUser && Array.isArray(embeddedUser.groups)) ? embeddedUser.groups : [];
    console.log('[CognitoLogin] Source B (login response embedded):', JSON.stringify(embeddedGroups));
    
    // Source C: getUser() API call (may return empty groups if AWS backend doesn't include them)
    let getUserData: ExternalUserData | null = null;
    let getUserGroups: string[] = [];
    try {
      getUserData = await externalAuthAPI.getUser();
      getUserGroups = getUserData.groups || [];
      console.log('[CognitoLogin] Source C (getUser API):', JSON.stringify(getUserGroups));
    } catch (getUserErr) {
      console.warn('[CognitoLogin] getUser() failed (non-fatal):', getUserErr);
    }
    
    // Merge groups: prioritize JWT > embedded > getUser. Use first non-empty source.
    const mergedGroups = jwtGroups.length > 0 ? jwtGroups
      : embeddedGroups.length > 0 ? embeddedGroups
      : getUserGroups;
    
    console.log('[CognitoLogin] Final merged groups:', JSON.stringify(mergedGroups));
    
    // Build userData from the best available source
    let userData: ExternalUserData;
    if (getUserData) {
      userData = { ...getUserData, groups: mergedGroups };
    } else if (embeddedUser) {
      userData = {
        username: embeddedUser.username || embeddedUser.email || email,
        email: embeddedUser.email || email,
        emailVerified: embeddedUser.emailVerified ?? false,
        groups: mergedGroups,
        attributes: embeddedUser.attributes || {},
      };
    } else {
      // Last resort: build minimal userData from JWT
      let tokenEmail = email;
      let tokenUsername = email;
      try {
        const idToken = tokenManager.getIdToken();
        if (idToken) {
          const parts = idToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            tokenEmail = payload.email || email;
            tokenUsername = payload['cognito:username'] || payload.sub || email;
          }
        }
      } catch { /* ignore */ }
      
      userData = {
        username: tokenUsername,
        email: tokenEmail,
        emailVerified: true,
        groups: mergedGroups,
        attributes: { email: tokenEmail },
      };
    }
    
    tokenManager.saveUserData(userData);

    // Step 3: Bridge into legacy auth
    const user = cognitoUserToLegacyUser(userData);
    authService.setCurrentUser(user);

    // Step 4: Ensure KV profile exists with all stats (fire-and-forget — don't block login)
    ensureUserProfile({
      id: user.id,
      email: userData.email,
      name: user.name,
      role: user.role,
      groups: userData.groups,
      phone: userData.attributes?.phone_number || '',
      cognitoUsername: userData.username,
    }).catch((err) => {
      // Silently handle - profile sync is non-critical for login
      // Detailed error already logged in ensureUserProfile
    });

    // Step 5: If user is an agent, bridge into agentSessionStorage for backward compat
    if (user.role === 'agent') {
      // Use idToken for API Gateway calls (not accessToken — API Gateway validates ID tokens)
      const token = tokenManager.getIdToken() || '';
      bridgeCognitoToAgentSession(user, token);
    }

    // Mark that this is a Cognito session
    authStorage.set(COGNITO_SESSION_FLAG, 'true');

    console.log('[CognitoLogin] Login successful for:', user.email, 'with role:', user.role);
    return { user, groups: userData.groups };
  } catch (err: any) {
    console.error('[CognitoLogin] Login failed:', err);
    
    // Log detailed error info for developers (console only)
    if (err.statusCode === 500 || err.statusCode === 502 || err.isServerError) {
      console.error('[CognitoLogin] 🔴 Server Error Details:', {
        statusCode: err.statusCode,
        isServerError: err.isServerError,
        devDetails: err.devDetails,
        originalError: err,
        timestamp: new Date().toISOString(),
        userEmail: email,
        troubleshooting: 'Check AWS CloudWatch logs at: https://console.aws.amazon.com/cloudwatch/'
      });
      
      // Provide a more specific user-facing message for login 500s
      const loginError = new Error(
        'The authentication server encountered an error. This is a known issue being resolved. ' +
        'Please try again shortly, or contact your administrator if it persists.'
      );
      (loginError as any).statusCode = 500;
      (loginError as any).isServerError = true;
      (loginError as any).devDetails = err.devDetails;
      throw loginError;
    }
    
    // Re-throw original error for other cases (bad credentials, etc.)
    throw err;
  }
}

/**
 * Register a new user in Cognito.
 * For seller self-registration: creates user + adds to 'sellers' group.
 * For admin user creation: creates user + adds to specified group.
 * After registration, auto-login to establish a session.
 */
export async function cognitoRegister(data: {
  email: string;
  password: string;
  name: string;
  group?: string;
  phone?: string;
}): Promise<{ user: User; groups: string[] }> {
  const group = data.group || 'seller'; // Default to seller for public registration

  // Step 1: Register the user — sellers use /auth/register-seller (no role field)
  const registerResult = group === 'seller'
    ? await externalAuthAPI.registerSeller({
        email: data.email,
        password: data.password,
        name: data.name,
        phone: data.phone,
      })
    : await externalAuthAPI.register({
        email: data.email,
        password: data.password,
        name: data.name,
        group,
        phone: data.phone,
      });

  // Step 2: If the API explicitly says the user is not confirmed,
  // throw a recognizable error so the UI can show the verification view
  // instead of attempting a login that will fail.
  if (registerResult.userConfirmed === false) {
    const err = new Error('User is not confirmed. Please check your email for a verification code.');
    throw err;
  }

  // Step 3: Login with the new credentials to establish a session
  // (only reached if user was auto-confirmed or userConfirmed was not returned)
  const registerLoginResult = await externalAuthAPI.login(data.email, data.password);
  tokenStorage.setTokens({
    idToken: registerLoginResult.idToken,
    accessToken: registerLoginResult.accessToken,
    refreshToken: registerLoginResult.refreshToken,
    expiresIn: registerLoginResult.expiresIn ?? 3600,
  });

  // Step 4: Fetch user profile (groups, attributes)
  const userData = await externalAuthAPI.getUser();
  tokenManager.saveUserData(userData);

  // Step 5: Bridge into legacy auth
  const user = cognitoUserToLegacyUser(userData);
  authService.setCurrentUser(user);

  // Step 6: Create KV profile with stats for the new user
  try {
    await ensureUserProfile({
      id: user.id,
      email: userData.email,
      name: user.name,
      role: user.role,
      groups: userData.groups,
      phone: data.phone || userData.attributes?.phone_number || '',
      cognitoUsername: userData.username,
    });
  } catch (err) {
    console.warn('[CognitoRegister] Non-critical: failed to ensure KV profile:', err);
  }

  // Mark that this is a Cognito session
  authStorage.set(COGNITO_SESSION_FLAG, 'true');

  return { user, groups: userData.groups };
}

/**
 * Confirm a Cognito signup with the verification code emailed to the user.
 * After confirmation, auto-login to establish a session.
 */
export async function cognitoConfirmSignup(
  email: string,
  code: string,
  password: string,
): Promise<{ user: User; groups: string[] }> {
  // Step 1: Confirm the signup with the verification code
  await externalAuthAPI.confirmSignup(email, code);

  // Step 2: Now login since the user is confirmed
  return cognitoLogin(email, password);
}

/**
 * Resend the Cognito verification code to the user's email.
 */
export async function cognitoResendCode(email: string): Promise<{ message: string }> {
  return externalAuthAPI.resendConfirmationCode(email);
}

/**
 * Check if a login error is due to the user not being confirmed.
 */
export function isUserNotConfirmedError(error: any): boolean {
  const msg = error?.message || error?.toString() || '';
  const lower = msg.toLowerCase();
  return (
    lower.includes('user is not confirmed') ||
    lower.includes('usernotconfirmedexception') ||
    lower.includes('not confirmed') ||
    lower.includes('user not confirmed')
  );
}

/**
 * Admin: Create a user in Cognito (does NOT auto-login).
 * Returns the Cognito username for reference.
 */
export async function cognitoAdminCreateUser(data: {
  email: string;
  password?: string;
  name: string;
  role: string;
  phone?: string;
}): Promise<{ username: string; email: string }> {
  const group = ROLE_TO_GROUP[data.role] || 'seller';

  const result = await externalAuthAPI.adminCreateUser({
    email: data.email,
    password: data.password,
    name: data.name,
    group,
    phone: data.phone,
  });

  return { username: result.username, email: result.email };
}

/**
 * Admin: Delete a user from Cognito.
 */
export async function cognitoAdminDeleteUser(username: string): Promise<void> {
  await externalAuthAPI.adminDeleteUser(username);
}

/**
 * Logout from Cognito and the legacy auth service.
 */
export async function cognitoLogout(): Promise<void> {
  try {
    await externalAuthAPI.logout();
  } catch (err) {
    console.error('Cognito logout error:', err);
  }

  authStorage.remove(COGNITO_SESSION_FLAG);
  authService.logout();
}

/**
 * Check if current session is a Cognito session.
 */
export function isCognitoSession(): boolean {
  return authStorage.get<string>(COGNITO_SESSION_FLAG) === 'true';
}

/**
 * Restore a Cognito session on app load.
 * Returns the User if session is valid, null otherwise.
 */
export async function restoreCognitoSession(): Promise<User | null> {
  if (!isCognitoSession()) return null;
  // ✅ Phase 2: hasSession() is now async
  if (!(await tokenManager.hasSession())) {
    authStorage.remove(COGNITO_SESSION_FLAG);
    return null;
  }

  try {
    // Ensure we have a valid token
    const validToken = await tokenManager.getValidToken();
    if (!validToken) {
      authStorage.remove(COGNITO_SESSION_FLAG);
      tokenManager.clearAll();
      return null;
    }

    // Fetch fresh user data from the external API
    // Fall back to stored user data if getUser() fails (e.g., network error)
    let userData: ExternalUserData;
    try {
      userData = await externalAuthAPI.getUser();
      
      // If getUser() succeeded but returned empty groups, enrich with JWT-decoded groups
      if (!userData.groups || userData.groups.length === 0) {
        console.warn('[RestoreSession] getUser() returned empty groups — enriching with JWT decode');
        const jwtGroups = extractGroupsFromStoredTokens();
        if (jwtGroups.length > 0) {
          console.log('[RestoreSession] Enriched groups from JWT:', jwtGroups);
          userData = { ...userData, groups: jwtGroups };
        }
      }
      
      tokenManager.saveUserData(userData);
    } catch (getUserErr) {
      console.warn('[RestoreSession] getUser() failed, trying stored user data:', getUserErr);
      const stored = tokenManager.getUserData();
      if (stored) {
        console.log('[RestoreSession] Using stored user data:', stored.email, 'groups:', stored.groups);
        userData = stored;
      } else {
        // Last resort: decode the JWT token client-side
        console.warn('[RestoreSession] No stored user data — attempting JWT decode');
        const groups = extractGroupsFromStoredTokens();
        // Decode basic info from the token
        let tokenEmail = '';
        let tokenUsername = '';
        let tokenName = '';
        try {
          const parts = validToken.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            tokenEmail = payload.email || '';
            tokenUsername = payload['cognito:username'] || payload.sub || '';
            tokenName = payload.name || '';
          }
        } catch { /* ignore */ }
        
        if (!tokenEmail) {
          throw getUserErr; // Can't recover without basic user info
        }
        
        userData = {
          username: tokenUsername,
          email: tokenEmail,
          emailVerified: true,
          groups,
          attributes: { email: tokenEmail, name: tokenName },
        };
      }
    }

    const user = cognitoUserToLegacyUser(userData);
    authService.setCurrentUser(user);

    // If user is an agent, bridge into agentSessionStorage for backward compat
    if (user.role === 'agent') {
      bridgeCognitoToAgentSession(user, validToken);
    }

    // Ensure KV profile exists / bump lastLogin (fire-and-forget)
    ensureUserProfile({
      id: user.id,
      email: userData.email,
      name: user.name,
      role: user.role,
      groups: userData.groups,
      phone: userData.attributes?.phone_number || '',
      cognitoUsername: userData.username,
    }).catch((err) => console.warn('[RestoreSession] Non-critical: failed to ensure KV profile:', err));

    return user;
  } catch (err) {
    console.error('Failed to restore Cognito session:', err);
    // Session is invalid — clean up
    authStorage.remove(COGNITO_SESSION_FLAG);
    tokenManager.clearAll();
    agentSessionStorage.clearSession();
    return null;
  }
}

/**
 * Get the Cognito access token for authenticated API calls.
 * Returns null if not a Cognito session or token is unavailable.
 */
export async function getCognitoToken(): Promise<string | null> {
  if (!isCognitoSession()) return null;
  return tokenManager.getValidToken();
}

/**
 * Change the current user's password via Cognito.
 * Per API docs: POST /auth/change-password
 */
export async function cognitoChangePassword(
  oldPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  return externalAuthAPI.changePassword(oldPassword, newPassword);
}

/**
 * Update the current user's profile in Cognito.
 * Per API docs: PUT /auth/profile
 */
export async function cognitoUpdateProfile(data: {
  name?: string;
  phoneNumber?: string;
}): Promise<{ message: string; user: any }> {
  const result = await externalAuthAPI.updateProfile(data);

  // Re-fetch user data so local session reflects the update
  try {
    const userData = await externalAuthAPI.getUser();
    tokenManager.saveUserData(userData);
    const user = cognitoUserToLegacyUser(userData);
    authService.setCurrentUser(user);
  } catch {
    // Non-critical — local session will be stale until next refresh
  }

  return result;
}

/**
 * Delete the current user's account via Cognito.
 * Per API docs: DELETE /auth/profile
 */
export async function cognitoDeleteAccount(): Promise<void> {
  await externalAuthAPI.deleteAccount();
  authStorage.remove(COGNITO_SESSION_FLAG);
  authService.logout();
}

/**
 * Initiate forgot password flow — sends a verification code to the user's email.
 */
export async function cognitoForgotPassword(email: string): Promise<{ message: string }> {
  return externalAuthAPI.forgotPassword(email);
}

/**
 * Confirm forgot password — resets the user's password with the code + new password.
 */
export async function cognitoConfirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<{ message: string }> {
  return externalAuthAPI.confirmForgotPassword(email, code, newPassword);
}

/**
 * Admin: Update a user's group memberships.
 * Per API docs: PUT /auth/users/role
 */
export async function cognitoUpdateUserRole(
  userId: string,
  groups: string[],
): Promise<{ message: string; userId: string; groups: string[]; previousGroups: string[] }> {
  return externalAuthAPI.updateUserRole(userId, groups);
}